import { pointerFromATag, tagNameEquals } from '@/lib/tag'
import { cn } from '@/lib/utils'
import type { Event } from '@nostr/tools/wasm'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  badgeDisplayName,
  parseBadgeDefinition,
  parseBadgePairs,
  useBadgeDefinition
} from '../Note/badge-utils'

function useDefinitionName(event: Event): string | undefined {
  const pointer = useMemo(() => {
    const aTag = event.tags.find(tagNameEquals('a'))
    return aTag ? pointerFromATag(aTag) : undefined
  }, [event])
  const { value } = useBadgeDefinition(pointer)
  return value ? badgeDisplayName(parseBadgeDefinition(value)) : undefined
}

export function BadgeDefinitionPreview({ event, className }: { event: Event; className?: string }) {
  const { t } = useTranslation()
  const meta = parseBadgeDefinition(event)
  const name = badgeDisplayName(meta)
  return (
    <div className={cn('truncate text-sm', className)}>
      {t('Badge')}: {name ?? meta.d ?? `#${event.kind}`}
    </div>
  )
}

export function BadgeAwardPreview({ event, className }: { event: Event; className?: string }) {
  const { t } = useTranslation()
  const name = useDefinitionName(event)
  const count = event.tags.filter((tag) => tag[0] === 'p').length
  return (
    <div className={cn('truncate text-sm', className)}>
      {t('Awarded {{name}} to {{count}} {{count, plural, one {user} other {users}}}', {
        name: name ?? t('a badge'),
        count
      })}
    </div>
  )
}

export function ProfileBadgesPreview({ event, className }: { event: Event; className?: string }) {
  const { t } = useTranslation()
  const { pairs, setRefs } = useMemo(() => parseBadgePairs(event.tags), [event])
  const total = pairs.length + setRefs.length
  const { value: first } = useBadgeDefinition(pairs[0]?.definition)
  const firstName = first ? badgeDisplayName(parseBadgeDefinition(first)) : undefined
  return (
    <div className={cn('truncate text-sm', className)}>
      {total === 0
        ? t('No badges')
        : total === 1 && firstName
          ? `${t('Badge')}: ${firstName}`
          : t('{{count}} badges', { count: total })}
    </div>
  )
}

export function BadgeSetPreview({ event, className }: { event: Event; className?: string }) {
  const { t } = useTranslation()
  const d = event.tags.find(tagNameEquals('d'))?.[1]
  const title = event.tags.find(tagNameEquals('title'))?.[1] || d
  const { pairs } = useMemo(() => parseBadgePairs(event.tags), [event])
  return (
    <div className={cn('truncate text-sm', className)}>
      {t('Badge set')}: {title ?? `#${event.kind}`} ({pairs.length})
    </div>
  )
}
